'use client'
import type { AnimationDefinition } from 'motion/react'
import { type Provider, useCallback, useState } from 'react'
import { useControllableState } from '../../hooks/use-controllable-state.ts'
import {
  createContext,
  getTypedProvider,
  getTypedRequiredUseContext,
} from '../../utils/context.ts'
import { useFormFieldControl } from '../form-field/index.ts'
import type { SelectOptionData, UseSelectProps } from './types.ts'
import { isItemVisible, optionHeight } from './utils.ts'

const initialOptionCount = 100

export function useSelect<ValueType extends string = string>(
  props: UseSelectProps<ValueType>
) {
  const {
    addButton,
    dataTestId,
    dataTrackingId,
    defaultValue,
    disabled,
    hasError: hasErrorProp,
    hasSuccess: hasSuccessProp,
    hasWarning: hasWarningProp,
    id,
    onClose,
    onOpen,
    onSearch,
    options: optionsProp,
    placeholder,
    readOnly,
    searchable = true,
    searchPlaceholder,
    value: valueProp,
  } = props

  const [filteredOptions, setFilteredOptions] =
    useState<SelectOptionData<ValueType>[]>(optionsProp)

  const handleValueSet = (value: ValueType | null) => {
    if (props.deselectable) {
      props.onChange(value)
    } else {
      if (value === null) {
        throw new Error('Value cannot be null')
      }

      props.onChange(value)
    }
  }

  const [openComplete, setOpenComplete] = useState(false)

  const [value, setValue] = useControllableState<ValueType | null>({
    defaultValue,
    value: valueProp,
    onChange: handleValueSet,
  })

  const formFieldProps = useFormFieldControl<HTMLDivElement>({
    dataTestId,
    dataTrackingId,
    disabled,
    id,
    hasError: hasErrorProp,
    hasSuccess: hasSuccessProp,
    hasWarning: hasWarningProp,
    readOnly,
  })

  const getInitialOptions = () => optionsProp.slice(0, initialOptionCount)

  const [search, setSearch] = useState('')
  const [options, setOptions] = useState<SelectOptionData<ValueType>[]>(
    getInitialOptions()
  )

  const setSearchResults = useCallback(
    (search: string) => {
      const newFilteredOptions = optionsProp.filter((option) =>
        isItemVisible(option, search)
      )
      setFilteredOptions(newFilteredOptions)
      setOptions(newFilteredOptions.slice(0, initialOptionCount))
      setSearch(search)
    },
    [optionsProp]
  )

  const handleSearch = useCallback(
    (search: string) => {
      setSearchResults(search)
      onSearch?.(search)
    },
    [setSearchResults, onSearch]
  )

  const handleLoadRemaining = useCallback(() => {
    setOptions(optionsProp)
  }, [optionsProp])

  const handleChange = (value: ValueType | null) => {
    setValue(value)
  }

  const handleClose = () => {
    setSearchResults('')
    onClose?.()
  }

  const handleOpen = () => {
    onOpen?.()
  }

  const handleAnimationComplete = (definition: AnimationDefinition) => {
    setOpenComplete(definition === 'open')

    if (definition === 'close') {
      setOptions(getInitialOptions())
    }
  }

  return {
    addButton,
    allOptions: optionsProp,
    filteredOptions,
    dataTestId,
    dataTrackingId,
    deselectable: props.deselectable,
    formFieldProps,
    initialOptionCount,
    onAnimationComplete: handleAnimationComplete,
    onChange: handleChange,
    onLoadRemaining: handleLoadRemaining,
    onClose: handleClose,
    onOpen: handleOpen,
    onSearch: handleSearch,
    openComplete,
    optionHeight,
    options,
    placeholder,
    search,
    searchable,
    searchPlaceholder,
    setSearch,
    value,
  }
}

export type UseSelectReturn<ValueType extends string = string> = ReturnType<
  typeof useSelect<ValueType>
>

const [UntypedSelectProvider, useUntypedSelectContext] = createContext<
  UseSelectReturn<string>
>({ name: 'SelectContext' })

export const getSelectProvider = <ValueType extends string = string>() => {
  return getTypedProvider<UseSelectReturn<ValueType>>(UntypedSelectProvider)
}

export function useSelectContext<ValueType extends string = string>() {
  return getTypedRequiredUseContext<UseSelectReturn<ValueType>>(
    useUntypedSelectContext
  )()
}

export type UseSelectInstanceReturn<ValueType extends string = string> = [
  UseSelectReturn<ValueType>,
  Provider<UseSelectReturn<ValueType>>,
]

export function useSelectInstance<ValueType extends string = string>(
  props: UseSelectProps<ValueType>
) {
  const context = useSelect<ValueType>(props)

  return [
    context,
    getSelectProvider<ValueType>(),
  ] as UseSelectInstanceReturn<ValueType>
}
